import type { VariantType } from "@upsquad/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import upsquadLogo from "@/src/common/assets/images/upsquad-full.png";
import { TestimonialsCarousel } from "@/src/common/components/testimonials-carousel";
import { env } from "@/src/common/constants/env";
import { getSubdomain } from "@/src/common/lib/subdomain";
import { useGetPublicCommunityQuery } from "@/src/common/services/community-public-http.service";

const VARIANT_GRADIENTS: Record<VariantType, string> = {
	default: "bg-linear-to-br from-blue-200 via-blue-300 to-blue-400",
	coral: "bg-linear-to-br from-orange-200 via-rose-300 to-rose-400",
	green: "bg-linear-to-br from-green-200 via-green-300 to-emerald-400",
	sky: "bg-linear-to-br from-sky-200 via-cyan-300 to-blue-400",
};

const DEFAULT_TESTIMONIALS = [
	{ quote: "testimonial_1_quote", author_name: "testimonial_1_name", author_role: "testimonial_1_role" },
	{ quote: "testimonial_2_quote", author_name: "testimonial_2_name", author_role: "testimonial_2_role" },
] as const;

export function AuthLayout({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation("auth");
	const subdomain = getSubdomain();
	const isCommunitySubdomain = subdomain !== null;

	const { data: community } = useGetPublicCommunityQuery(subdomain ?? "", {
		skip: !isCommunitySubdomain,
	});

	const coverImages = community?.cover_images ?? [];
	const testimonials = community?.testimonials ?? [];
	const hasCoverImages = coverImages.length > 0;
	const hasTestimonials = testimonials.length > 0;

	const gradientClass = community?.variant ? VARIANT_GRADIENTS[community.variant] : VARIANT_GRADIENTS.default;

	const [coverIndex, setCoverIndex] = useState(0);

	useEffect(() => {
		const count = coverImages.length;
		if (count <= 1) return;
		const interval = setInterval(() => {
			setCoverIndex((prev) => (prev + 1) % count);
		}, 5000);
		return () => clearInterval(interval);
	}, [coverImages.length]);

	const sortedCovers = [...coverImages].sort((a, b) => a.order - b.order);
	const sortedTestimonials = [...testimonials].sort((a, b) => a.order - b.order);

	const fallbackTestimonials = DEFAULT_TESTIMONIALS.map((item) => ({
		quote: t(item.quote),
		author_name: t(item.author_name),
		author_role: t(item.author_role),
		order: 0,
	}));

	const displayTestimonials = hasTestimonials ? sortedTestimonials : fallbackTestimonials;

	const logoSrc = community?.image ?? upsquadLogo;
	const logoAlt = community?.name ?? env.APP_NAME;

	return (
		<div className="flex min-h-screen">
			<div
				className={`hidden md:flex md:w-1/2 relative overflow-hidden m-2 rounded-lg ${gradientClass} bg-size-[200%_200%] animate-[gradient-shift_8s_ease_infinite]`}
			>
				{hasCoverImages && (
					<img
						src={sortedCovers[coverIndex]?.url}
						alt={sortedCovers[coverIndex]?.alt ?? ""}
						className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[35vh] w-full object-cover"
					/>
				)}
				<div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-black/20" />
				<div className="absolute top-6 left-6 z-10">
					<img src={logoSrc} alt={logoAlt} className="h-8 object-contain" />
				</div>
				<div className="relative z-10 flex flex-col justify-end p-12 w-full">
					<TestimonialsCarousel testimonials={displayTestimonials} />
				</div>
				{isCommunitySubdomain && (
					<div className="absolute bottom-4 right-6 z-10">
						<p className="text-xs text-white/60">{t("powered_by", { appName: env.APP_NAME })}</p>
					</div>
				)}
			</div>
			<div className="flex-1 flex items-center justify-center p-6 md:p-12">
				<div className="absolute left-1/2 top-6 -translate-x-1/2 md:hidden">
					<img src={logoSrc} alt={logoAlt} className="h-8 object-contain" />
				</div>
				<div className="w-full max-w-md">{children}</div>
			</div>
		</div>
	);
}
